import axiosInstance from '../axios';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { TAddTopicsBody } from '../../components/Timeline/SendClipModal';
import { getTokenFromLocalStorage } from '../../helpers';
import { TOPIC } from '../../enums';
import { Mode } from '../../components/Composer/ComposerDownload';
import { getActiveCommentThreads, getVisibleFeedComments } from '../../helpers/comments';

type NewComposerBody = {
  title: string;
  videofeedId: string;
  topicsIds?: string[];
  clipsIds?: string[];
};

export const postCreateNewComposer = async (data: NewComposerBody) => {
  const res = await axiosInstance.post(API_ROUTES.REFLEX_COMPOSER, data);
  return res;
};

export const getComposerDataByVideoFeedId = async (videoFeedId: string, config?: CacheRequestConfig) => {
  const res = await axiosInstance.get(`${API_ROUTES.VIDEOFEED}/${videoFeedId}`, config);
  return { ...res, data: getVisibleFeedComments(res.data) };
};

export const applyClipOrderFromFrontend = async (videoFeedId: string, payload: { topics: { topicId: number; clipIds: number[] }[] }) => {
  const res = await axiosInstance.post(`${API_ROUTES.REFLEX_COMPOSER}/${videoFeedId}/apply-clip-order`, payload);
  return res;
};

export const applyAngleOrderFromFrontend = (composerId: string, payload: any) =>
  axiosInstance.post(`${API_ROUTES.REFLEX_COMPOSER}/${composerId}/apply-angle-order`, payload);

export const getComposerAnglesZipUrl = async (videoFeedId: string) => {
  const res = await axiosInstance.get(`${API_ROUTES.REFLEX_COMPOSER}/composer/${videoFeedId}/angles/zip`);
  return res;
};

export const getVideofeedAccessess = async (videoFeedId: string, includeInherited = false) => {
  const res = await axiosInstance.get(
    `${API_ROUTES.VIDEOFEED_ACCESS}/${videoFeedId}${includeInherited ? '?includeInherited=true' : ''}`,
  );
  return res;
};

export const checkVideofeedAccess = async (videofeedId: string) => {
  const res = await axiosInstance.get(`${API_ROUTES.ACCESS_CHECK}?videofeedId=${videofeedId}`);
  return res.data;
};

export const getComposerDataByVideoFeedIdForVideoPage = async (
  videoFeedId: string,
  config?: CacheRequestConfig,
) => {
  const res = await axiosInstance.get(`${API_ROUTES.VIDEOFEED_VIDEO_PAGE}/${videoFeedId}`, config);
  return { ...res, data: getVisibleFeedComments(res.data) };
};

export const getVideofeedInfo = async (videoFeedId: string) => {
  const res = await axiosInstance.get(`${API_ROUTES.VIDEOFEED_INFO}/${videoFeedId}`);
  return res;
};

export const downloadComposerAVaultData = async (id: string) => {
  const token = getTokenFromLocalStorage();
  const base = process.env.REACT_APP_API_URL || '';
  const url = new URL(`${API_ROUTES.COMPOSER_DOWNLOAD_AVAULT}/${id}`, base).toString();

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/zip, application/octet-stream, */*',
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Composer AVault download failed (${res.status})`);
  }

  return await res.blob();
};

export const getComposerDataByVideoFeedIdForTimelineRow = async (videoFeedId: string) => {
  const res = await axiosInstance.get(`${API_ROUTES.VIDEOFEED_VIDEO_TIMELINE}/${videoFeedId}`);
  return { ...res, data: getVisibleFeedComments(res.data) };
};

export const getComposerDataByVideoFeedIdForVideoComments = async (videoFeedId: string) => {
  const config: CacheRequestConfig = { cache: false };
  const res = await axiosInstance.get(`${API_ROUTES.VIDEOFEED_VIDEO_COMMENTS}/${videoFeedId}`, config);
  return { ...res, data: getVisibleFeedComments(res.data) };
};

type TpatchReoderComposer = {
  topics: {
    id: string;
    clips: {
      id: string;
    }[];
  }[];
};

export const patchReoderComposer = async (reoderedTopics: TpatchReoderComposer, videoFeedId: string) => {
  const res = await axiosInstance.patch(`${API_ROUTES.VIDEOFEED_REORDER}/${videoFeedId}`, reoderedTopics);
  return res;
};

export const patchAddTopics = async (data: TAddTopicsBody) => {
  const res = await axiosInstance.patch(API_ROUTES.VIDEOFEED_SEND_CLIPS, data);
  return res;
};

export const deleteTopicInComposer = async (topicId: string, videoFeedId: string) => {
  const res = await axiosInstance.delete(`${API_ROUTES.COMPOSER_TOPIC}/${topicId}/${videoFeedId}`);
  return res;
};

export const createComposerTopic = async (name: string, videofeedId: string, index: number, topic?: TOPIC) => {
  const res = await axiosInstance.post(API_ROUTES.CREATE_COMPOSER_TOPIC, { name, videofeedId, index, topic });
  return res;
};

export const deleteComposerClip = async (videofeedId: string, clipIds: string[]) => {
  return await axiosInstance.post(`${API_ROUTES.REFLEX_COMPOSER_CLIP}/${videofeedId}`, {
    // @ts-ignore
    ids: clipIds,
  });
};

export const recreateclipsInComposer = async (args: any) => {
  return await axiosInstance.post(API_ROUTES.CREATE_COMPOSER_CLIP, args);
};

export const getClipCommentsByClipId = async (clipId: number) => {
  const res = await axiosInstance.get(`${API_ROUTES.CLIP_COMMENTS}/${clipId}`);
  return { ...res, data: getActiveCommentThreads(res.data) };
};

export const getClipsCommentsByClipIds = async (clipIds: number[]) => {
  const idsParam = clipIds.join(',');
  const config: CacheRequestConfig = {
    params: { ids: idsParam },
    cache: false,
  };
  const res = await axiosInstance.get(API_ROUTES.CLIPS_COMMENTS, config);
  return { ...res, data: getActiveCommentThreads(res.data) };
};

export const createComposerDownloadJob = async (
  feedId: string,
  opts?: {
    clipIds?: number[];
    angleId?: number;
    withJson?: boolean;
    startRequestId?: string;
  },
  fpsList?: any,
  signal?: AbortSignal,
) => {
  const body: any = {};
  if (Array.isArray(opts?.clipIds) && opts!.clipIds.length) {
    body.clipIds = opts!.clipIds.map(Number).filter(Number.isFinite);
  }
  if (Number.isFinite(Number(opts?.angleId))) {
    body.angleId = Number(opts!.angleId);
  }

  body.withJson = opts?.withJson;
  body.fpsList = fpsList;
  if (opts?.startRequestId) {
    body.startRequestId = String(opts.startRequestId);
  }

  const res = await axiosInstance.post(
    `/reflex-composer/download-composer/${feedId}`,
    body,
    signal ? { signal } : undefined,
  );
  return res?.data ?? res;
};

// Fire a MediaConvert export job for a composer and get back the job id
export const createComposerDownloadJobPerClip = async (
  videofeedId: string,
  body?: {
    angleId?: number;
    formatForAnglesImport?: boolean;
    startRequestId?: string;
  },
  signal?: AbortSignal,
) => {
  const res = await axiosInstance.post(
    `/reflex-composer/download-composer-per-clip/${videofeedId}`,
    body || {},
    signal ? { signal } : undefined,
  );
  return res.data;
};

// Fire MediaConvert export jobs for ONLY the specified clip IDs of a composer
export const createComposerDownloadJobPerClipIds = async (
  videofeedId: string,
  clipIds: number[],
  body?: {
    angleId?: number;
    formatForAnglesImport?: boolean;
    startRequestId?: string;
  },
  signal?: AbortSignal,
) => {
  const res = await axiosInstance.post(
    `/reflex-composer/download-composer-per-clip-ids/${videofeedId}`,
    { clipIds, ...(body || {}) },
    signal ? { signal } : undefined,
  );
  return res.data;
};

export const cancelComposerDownloadStart = async (startRequestId: string) => {
  return axiosInstance.post('/reflex-composer/download-start-cancel', {
    startRequestId,
  });
};

export const getRecentDownloadJobs = async (limit = 10, includeAwsLive = true) => {
  const res = await axiosInstance.get('/reflex-composer/download-jobs/recent', {
    params: { limit, includeAwsLive },
  });
  return res?.data ?? res;
};

export type GeneratedDownloadFile = {
  key: string;
  name: string;
  size: number;
  lastModified?: string;
  generatedBy?: string;
};

export type GeneratedDownloadType =
  | 'standaloneMovies'
  | 'standaloneMoviesWithPadding'
  | 'clips'
  | 'mergedTimelineVideos';

export type GeneratedDownloadFolder = {
  type: GeneratedDownloadType;
  count: number;
};

export type GeneratedDownloadClipComposer = {
  id: number;
  title: string;
  count: number;
};

export const getGeneratedDownloadFolders = async (): Promise<GeneratedDownloadFolder[]> => {
  const res = await axiosInstance.get('/reflex-composer/generated-downloads/folders');
  return res.data;
};

export const getGeneratedDownloadClipComposers = async (): Promise<GeneratedDownloadClipComposer[]> => {
  const res = await axiosInstance.get('/reflex-composer/generated-downloads/clip-composers');
  return res.data;
};

export const getGeneratedDownloads = async (params: {
  type: GeneratedDownloadType;
  page: number;
  limit: number;
  sort: string;
  order: string;
  composerId?: number;
}): Promise<{ documents: GeneratedDownloadFile[]; total: number }> => {
  const res = await axiosInstance.get('/reflex-composer/generated-downloads', { params });
  return res.data;
};

export const getGeneratedDownloadUrl = async (key: string, filename?: string) => {
  const res = await axiosInstance.post('/reflex-composer/generated-downloads/url', { key, filename });
  return res.data as { signedUrl: string; expiresAt: string };
};

export const deleteGeneratedDownload = async (key: string) => {
  return axiosInstance.delete('/reflex-composer/generated-downloads/file', {
    data: { key },
  });
};

export const getExportManifest = async (prefixS3Url: string, transcodeBatchUuid?: string | null) => {
  // backend reads/updates S3 manifest and returns progress
  return axiosInstance.get(`/reflex-composer/export-manifest`, {
    params: { prefix: prefixS3Url, ...(transcodeBatchUuid ? { transcode_batch_uuid: transcodeBatchUuid } : {}) },
  });
};

export const getExportFiles = async (prefixS3Url: string) => {
  return axiosInstance.get(`/reflex-composer/export-files`, { params: { prefix: prefixS3Url } });
};

export const getExportSizeEstimate = async (prefixS3Url: string) => {
  return axiosInstance.get(`/reflex-composer/export-size-estimate`, {
    params: { prefix: prefixS3Url },
  });
};

export const estimateComposerDownload = async (
  id: string,
  body: {
    mode?: Mode;
    clipIds?: number[];
    angleId?: number;
  },
) => {
  const res = await axiosInstance.post(`/reflex-composer/download-estimate/${id}`, body || {});
  return res?.data ?? res;
};

export const getGeneratedComposerClipIds = async (id: string, body?: { clipIds?: number[] }) => {
  const res = await axiosInstance.post(`/reflex-composer/download-generated-clips/${id}`, body || {});
  return res?.data ?? res;
};

export const downloadExportFileFetch = async (bucket: string, key: string) => {
  const token = getTokenFromLocalStorage();
  const base = process.env.REACT_APP_API_URL || '';

  const url = new URL('/api/reflex-composer/export-file', base);
  url.searchParams.set('bucket', bucket);
  url.searchParams.set('key', key);

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,

      Accept: '*/*',
    },

    redirect: 'follow',
  });

  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    const msg = ct.includes('application/json') ? JSON.stringify(await res.json()) : await res.text();
    throw new Error(`Download failed (${res.status}): ${msg}`);
  }

  const cd = res.headers.get('content-disposition') || '';
  let serverName = '';
  const m = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  if (m) serverName = decodeURIComponent(m[1].replace(/"/g, ''));

  const blob = await res.blob();
  return { blob, serverName };
};
